import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "./../img/logo-mh.svg";
import Page1 from "./../img/page-1.png";
import Page2 from "./../img/page-2.png";
import Oval from "./../img/oval.svg";
import Flash from "./../img/flash.svg";
import SpeechBubble from "./../img/speech-bubble.svg";
import AI from "./../img/artificial-intelligence.svg";

import LogIn from "../components/LogIn.tsx";
import SignUp from "../components/SignUp.tsx";
import SubscriptionPlan from "../components/SubscriptionPlan.tsx";
import ResetPasswordForm from "../components/ResetPasswordForm.tsx";

import "./../style/SignIn.css";
import "./../style/general.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showSubscriptionPlan, setShowSubscriptionPlan] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("isAuthenticated") === "true") {
      navigate("/leads");
    }

    if (location.hash === "#reset-password") {
      setShowResetPassword(true);
    }
    // eslint-disable-next-line
  }, [navigate]); // Remove location.hash from dependencies

  const handleLoginSuccess = () => {
    navigate("/leads");
  };

  const handleSignUpSuccess = () => {
    setShowSubscriptionPlan(true);
  };

  const handlePlanSelected = () => {
    setShowSubscriptionPlan(false);
    setIsSignUp(false);
  };

  const handleBackToLogin = () => {
    setShowResetPassword(false);
    window.history.replaceState(null, "", window.location.pathname);
  };


  return (
    <div className="min-h-screen overflow-auto">
      <section className="flex flex-col items-center justify-center text-center py-4 lg:py-16 px-4">
        <img
          src={Logo}
          alt="logo"
          className="mb-[42px] lg:mb-[124px] h-12 w-auto"
        />

        <h1 className="text-2xl lg:text-5xl font-semibold mb-[28px] lg:mb-[84px]">
          Next-Gen Lead Nurturing Platform
        </h1>

        <p className="text-xl lg:text-3xl font-semibold mb-[20px] lg:mb-[70px] max-w-xl text-[#3e3e3e]">
          Significantly improve lead conversion with personalized and fast
          responses
        </p>

        <button
          className="bg-[#407097] hover:bg-[#335c7a] text-white font-semibold text-sm lg:text-2sm px-20 lg:px-[102px] py-2 lg:py-[20px] rounded-full"
          onClick={() => {
            const el = document.getElementById("form-section");
            el?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          LET'S GET STARTED
        </button>
      </section>
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto flex justify-center">
          <img
            src={Page1}
            alt="App preview"
            className="w-full max-w-5xl rounded-lg shadow-md"
          />
        </div>
      </section>

      <div id="form-section" className="bg-white py-20 px-4">
        <div className="flex flex-col lg:flex-row justify-center items-center gap-y-10 lg:gap-x-16 max-w-6xl mx-auto">
          <div className="w-full lg:max-w-md text-center lg:text-left">
            <h2 className="text-lg sm:text-3xl font-semibold">
              Be the first to experience the future of real estate lead
              nurturing!
            </h2>
          </div>

          {showSubscriptionPlan ? (
            <SubscriptionPlan
              onPlanSelected={handlePlanSelected}
              onBack={() => setShowSubscriptionPlan(false)}
            />
          ) : showResetPassword ? (
            <ResetPasswordForm
              onBackToLogin={handleBackToLogin}
            />
          ) : isSignUp ? (
            <SignUp
              onSignUpSuccess={handleSignUpSuccess}
              onBackToLogin={() => setIsSignUp(false)}
            />
          ) : (
            <LogIn
              onLoginSuccess={handleLoginSuccess}
              onShowSignUp={() => setIsSignUp(true)}
            />
          )}
        </div>
      </div>
      <section className="py-10 lg:py-20 px-4">
        <div className="max-w-6xl mx-auto mb-4 lg:mb-16">
          <h2 className="text-5xl lg:text-7xl font-bold text-center text-[#383838]">
            The MyHomey Platform
          </h2>
        </div>

        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-6 lg:gap-20">
          <div className="w-full lg:w-4/12 text-center lg:text-left">
            <p className="text-xl lg:text-3xl text-gray-800 leading-relaxed font-semibold">
              Automatically extract and view key customer data points from all
              client communication channels in one place
            </p>
          </div>

          <div className="w-full lg:w-8/12">
            <img
              src={Page2}
              alt="Platform Preview"
              className="w-full rounded-lg shadow-md"
            />
          </div>
        </div>
      </section>
      <section className="py-10 lg:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xl lg:text-4xl font-medium text-gray-900 leading-relaxed text-[#3e3e3e]">
            Leverage cutting-edge lead nurturing technology to effectively
            engage higher quantities of leads and successfully nurture
            individual leads
          </p>
        </div>
      </section>
      <section className="bg-white py-10 lg:py-20 px-4">
        <div className="max-w-6xl mx-auto text-center mb-16 lg:mb-[70px]">
          <h2 className="lext-3xl lg:text-5xl font-semibold text-gray-900">
            The MyHomey Edge
          </h2>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 text-center">
          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-[264px] aspect-[264/211] mx-auto mb-6">
              <img
                src={Oval}
                alt=""
                className="absolute top-0 left-0 w-full h-full object-contain"
              />
              <img
                src={AI}
                alt="AI Icon"
                className="absolute inset-0 w-[70px] h-[215px] m-auto z-10"
              />
            </div>
            <h3 className="text-xl lg:text-2xl font-semibold mb-2 lg:mb-12">
              Specialized AI For You
            </h3>
            <p className="text-gray-700 text-base lg:text-lg">
              Utilize artificial intelligence (as much or as little as you want)
              that’s trained by thousands of agents and ISAs to collect key data
              points from clients to help you transact more and faster
            </p>
          </div>

          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-[264px] aspect-[264/211] mx-auto mb-6">
              <img
                src={Oval}
                alt=""
                className="absolute top-0 left-0 w-full h-full object-contain"
              />
              <img
                src={Flash}
                alt="AI Icon"
                className="absolute inset-0 w-[70px] h-[70px] m-auto z-10"
              />
            </div>
            <h3 className="text-xl lg:text-2xl font-semibold mb-2 lg:mb-12">
              Maximize Your Efficiency
            </h3>
            <p className="text-gray-700 text-base lg:text-lg">
              Leverage our customer scoring technology to{" "}
              <span className="font-medium">focus</span> your time on the most
              actionable leads
            </p>
          </div>

          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-[264px] aspect-[264/211] mx-auto mb-6">
              <img
                src={Oval}
                alt=""
                className="absolute top-0 left-0 w-full h-full object-contain"
              />
              <img
                src={SpeechBubble}
                alt="AI Icon"
                className="absolute inset-0 w-[70px] h-[70px] m-auto z-10"
              />
            </div>
            <h3 className="text-xl lg:text-2xl font-semibold mb-2 lg:mb-12">
              Omnichannel Communication
            </h3>
            <p className="text-gray-700 text-base lg:text-lg">
              Communicate with your leads across text message, phone, and e-mail
              channels in one place
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignIn;
